import { Start, Stop } from 'smg/buttons';
import { CreateFormSubmissionWithReCaptcha } from 'smg/customer-api/formsubmissions';
import { Session } from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { BlockPastDates, ReportValidity } from 'smg/form';
import * as models from 'smg/customer-api/models';
import { Phone } from 'smg/phone';
import { GetFirstAndLast } from 'smg/users';
import * as store from '../../free-quote/store';
import { Relref } from '../../hugo';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
}
declare const window: myWindow;

main();

function main() {
    store.Init();

    const $form = document.querySelector('#free-quote-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;
    const $submit = document.querySelector('button[type="submit"]') as HTMLButtonElement;

    const $name = document.querySelector('#free-quote-name') as HTMLInputElement;
    const $email = document.querySelector('#free-quote-email') as HTMLInputElement;
    const $phone = document.querySelector('#free-quote-phone') as HTMLInputElement;

    const p = new Phone($phone);

    const $eventDateNo = document.querySelector('#free-quote-event-date-no') as HTMLInputElement;
    const $eventDateYes = document.querySelector('#free-quote-event-date-yes') as HTMLInputElement;
    const $eventDateYesDesc = document.querySelector('#free-quote-event-date-yes-desc') as HTMLElement;
    const $eventDate = document.querySelector('#free-quote-event-date') as HTMLInputElement;
    const revalidateEventDate = BlockPastDates($eventDate, { earliest: 'tomorrow' });

    $eventDateNo.addEventListener('click', () => {
        $eventDateYesDesc.classList.add('d-none');
        $eventDate.disabled = true;
        revalidateEventDate();
    });
    $eventDateYes.addEventListener('click', () => {
        $eventDateYesDesc.classList.remove('d-none');
        $eventDate.disabled = false;
        revalidateEventDate();
    });

    $eventDate.addEventListener('change', () => ReportValidity($eventDate));

    const $budget1 = document.querySelector('#free-quote-budget-1') as HTMLInputElement;
    const $budget1Desc = document.querySelector('#free-quote-budget-1-desc') as HTMLElement;
    const $budget2 = document.querySelector('#free-quote-budget-2') as HTMLInputElement;
    const $budget2Desc = document.querySelector('#free-quote-budget-2-desc') as HTMLElement;
    const $budget3 = document.querySelector('#free-quote-budget-3') as HTMLInputElement;
    const $budget3Desc = document.querySelector('#free-quote-budget-3-desc') as HTMLElement;
    const $budget4 = document.querySelector('#free-quote-budget-4') as HTMLInputElement;
    const $budget4Desc = document.querySelector('#free-quote-budget-4-desc') as HTMLElement;

    $budget1.addEventListener('click', () => {
        $budget1Desc.classList.remove('d-none');
        $budget2Desc.classList.add('d-none');
        $budget3Desc.classList.add('d-none');
        $budget4Desc.classList.add('d-none');
    });
    $budget2.addEventListener('click', () => {
        $budget1Desc.classList.add('d-none');
        $budget2Desc.classList.remove('d-none');
        $budget3Desc.classList.add('d-none');
        $budget4Desc.classList.add('d-none');
    });
    $budget3.addEventListener('click', () => {
        $budget1Desc.classList.add('d-none');
        $budget2Desc.classList.add('d-none');
        $budget3Desc.classList.remove('d-none');
        $budget4Desc.classList.add('d-none');
    });
    $budget4.addEventListener('click', () => {
        $budget1Desc.classList.add('d-none');
        $budget2Desc.classList.add('d-none');
        $budget3Desc.classList.add('d-none');
        $budget4Desc.classList.remove('d-none');
    });

    void Session().then(sess => {
        $name.value = sess.user.fullName();
        $email.value = sess.user.emailAddress.email;
        return p.ready().then(() =>
            p.iti.setNumber(sess.user.phoneNumber.phone));
    }).catch(err => console.warn(err)).then(() => {
        const settings = store.Get();
        if (settings.getStarted) {
            $name.value = settings.getStarted.name;
            $email.value = settings.getStarted.email;
            p.iti.setNumber(settings.getStarted.phone);
            if (settings.getStarted.hasEventDate) {
                $eventDateYes.click();
                $eventDateYes.dispatchEvent(new Event('click'));
                $eventDate.value = settings.getStarted.eventDate;
                // A restored date can have gone stale while the funnel sat in
                // localStorage; setting .value fires no input event.
                revalidateEventDate();
            } else {
                $eventDateNo.click();
                $eventDateNo.dispatchEvent(new Event('click'));
            }
            switch (settings.getStarted.budgetLevel) {
            case 1:
                $budget1.click();
                $budget1.dispatchEvent(new Event('click'));
                break;
            case 2:
                $budget2.click();
                $budget2.dispatchEvent(new Event('click'));
                break;
            case 3:
                $budget3.click();
                $budget3.dispatchEvent(new Event('click'));
                break;
            case 4:
                $budget4.click();
                $budget4.dispatchEvent(new Event('click'));
                break;
            }
        }
    });

    $form.noValidate = true;
    $form.addEventListener('submit', async e => {
        e.preventDefault();

        if (!ReportValidity($form)) {
            return;
        }

        Start($submit);
        $fieldset.disabled = true;

        // The capture persists as an `email-capture` form submission (#7260);
        // the routing automation does the user create/attach + flag
        // assignment, so advancing the funnel waits only on the submission.
        await CreateFormSubmissionWithReCaptcha({
            form: 'email-capture',
            variant: 'free-quote-get-started',
            payload: {
                email: $email.value,
                flagIDs: [models.FlagID.FLAG_PRE_FORM_EMAIL],
            },
        }, {
            $reCaptchaPlaceholder: $form.querySelector('.contact-recpatcha-parent') as HTMLElement,
            action: 'email_capture',
        }).then(async () => {
            const sess = await Session().catch(() => undefined);

            const { firstName, lastName } = GetFirstAndLast($name.value);

            const dataLayer = window.dataLayer || [];
            dataLayer.push({
                'event': 'hugo-pre-form-email-submit',
                'userData': {
                    'email': sess?.user.emailAddress.email || $email.value,
                    'phone': sess?.user.phoneNumber.phone,
                    'firstName': sess?.user.firstName || firstName,
                    'lastName': sess?.user.lastName || lastName,
                },
            });

            let budgetLevel: 1 | 2 | 3 | 4;
            let budgetDesc: string;
            if ($budget1.checked) {
                budgetLevel = 1;
                budgetDesc = $budget1Desc.textContent ?? '';
            } else if ($budget2.checked) {
                budgetLevel = 2;
                budgetDesc = $budget2Desc.textContent ?? '';
            } else if ($budget3.checked) {
                budgetLevel = 3;
                budgetDesc = $budget3Desc.textContent ?? '';
            } else {
                budgetLevel = 4;
                budgetDesc = $budget4Desc.textContent ?? '';
            }

            const settings = store.Get();
            settings.getStarted = {
                name: $name.value,
                email: $email.value,
                phone: p.iti.getNumber(),
                hasEventDate: $eventDateYes.checked,
                eventDate: $eventDate.value,
                budgetLevel,
                budgetDesc,
            };
            store.Set(settings);

            if (window.location.href.includes('american-made')) {
                window.location.href = Relref('/american-made-free-quote/details');
            } else {
                window.location.href = Relref('/free-quote/details');
            }
        }).catch(err => {
            ShowError(err);

            Stop($submit);
            $fieldset.disabled = false;
        });
    });
}
